/**
 * Assembly Bundle (atomic, native cart)
 *
 * For install-required outdoor-kitchen products the $1,450 assembly fee must be
 * in the cart with the kitchen. The old observer approach added the kitchen,
 * then fired a SECOND /cart/add.js ~800ms later — so the drawer opened, then
 * the cart "settled" over 1-2s as the assembly + price appeared.
 *
 * This version intercepts add-to-cart and submits the kitchen + assembly in ONE
 * /cart/add.js call (requesting the cart sections in the same response), then
 * dispatches the theme's cart:update event so the NATIVE drawer auto-opens and
 * morphs with the complete cart immediately — one round trip, no settle delay.
 */
(function () {
  'use strict';

  var CART_UPDATE_EVENT = 'cart:update'; // ThemeEvents.cartUpdate

  // Only the product-page block (snippets/installation-package.liquid). Cart rows
  // also carry data-install-variant-id, and matching them would hijack the cart
  // form's checkout submit.
  function getInstallEl(scope) {
    return (scope || document).querySelector('.install-package[data-install-variant-id]');
  }

  function getCartSectionIds() {
    var ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach(function (el) {
      var id = el.getAttribute('data-section-id');
      if (id && ids.indexOf(id) === -1) ids.push(id);
    });
    return ids;
  }

  /** Collect line-item properties[...] inputs (Cabinet Colour, Gas Type, etc.). */
  function collectProperties(form) {
    var props = {};
    form.querySelectorAll('[name^="properties["]').forEach(function (el) {
      if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
      if (el.value == null || el.value === '') return;
      var m = el.name.match(/^properties\[(.+)\]$/);
      if (m) props[m[1]] = el.value;
    });
    return props;
  }

  /** Read the live item count from a rendered section's hidden [ref="cartItemCount"]. */
  function parseItemCountFromSections(sections) {
    if (!sections) return null;
    for (var key in sections) {
      if (!Object.prototype.hasOwnProperty.call(sections, key)) continue;
      try {
        var doc = new DOMParser().parseFromString(sections[key], 'text/html');
        var el = doc.querySelector('[ref="cartItemCount"]');
        if (el) return parseInt(el.textContent, 10);
      } catch (e) { /* ignore */ }
    }
    return null;
  }

  async function postAddWithBackoff(body, attempt) {
    attempt = attempt || 0;
    var resp = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (resp.status === 429 && attempt < 3) {
      await new Promise(function (r) { setTimeout(r, 800 * Math.pow(2, attempt)); });
      return postAddWithBackoff(body, attempt + 1);
    }
    return resp;
  }

  function openCartDrawer() {
    // Dispatching cart:update already auto-opens the drawer (if auto_open is on);
    // also click the drawer's own open button as a fallback.
    var drawer = document.querySelector('cart-drawer-component');
    if (!drawer) return;
    var openBtn = drawer.querySelector('[on\\:click="/open"], button');
    if (openBtn) { try { openBtn.click(); } catch (e) { /* no-op */ } }
  }

  function dispatchCartUpdate(itemCount, sections) {
    document.dispatchEvent(new CustomEvent(CART_UPDATE_EVENT, {
      bubbles: true,
      detail: {
        resource: {},
        sourceId: 'assembly-bundle',
        data: { source: 'assembly-bundle', itemCount: itemCount, sections: sections }
      }
    }));
  }

  function flashAdded(button) {
    if (!button) return;
    button.classList.add('atc-added');
    setTimeout(function () { button.classList.remove('atc-added'); }, 1500);
  }

  /** Show/hide a loading overlay on the cart items while the add is in flight. */
  function setCartLoading(loading) {
    document.querySelectorAll('cart-items-component').forEach(function (el) {
      el.classList.toggle('cart-items--loading', !!loading);
    });
  }

  function showFormError(form, message) {
    var errEl = form.querySelector('[ref="addToCartTextError"]');
    if (!errEl || !message) return;
    errEl.classList.remove('hidden');
    var node = document.createTextNode(' ' + message);
    errEl.appendChild(node);
    setTimeout(function () {
      errEl.classList.add('hidden');
      if (node.parentNode) node.parentNode.removeChild(node);
    }, 8000);
  }

  async function triggerAtomicAdd(form, button) {
    if (form.__assemblyInFlight) return;

    var installEl = getInstallEl(form);
    var installVariantId = installEl && installEl.dataset.installVariantId;
    var variantInput = form.querySelector('[name="id"]');
    var variantId = variantInput && variantInput.value;
    if (!installVariantId || !variantId) {
      console.warn('[AssemblyBundle] Missing variant or install id; aborting.');
      return;
    }

    form.__assemblyInFlight = true;
    if (button) button.disabled = true;

    // Optimistic: open the drawer immediately (with a loading state) so it feels
    // instant, then fill it when the atomic add returns.
    openCartDrawer();
    setCartLoading(true);

    try {
      var qtyInput = form.querySelector('[name="quantity"]');
      var qty = qtyInput && parseInt(qtyInput.value, 10) > 0 ? parseInt(qtyInput.value, 10) : 1;

      var kitchenItem = { id: parseInt(variantId, 10), quantity: qty };
      var kitchenProps = collectProperties(form);
      if (Object.keys(kitchenProps).length) kitchenItem.properties = kitchenProps;

      var items = [
        kitchenItem,
        {
          id: parseInt(installVariantId, 10),
          quantity: qty,
          properties: { '_bundled_with': 'Outdoor Kitchen Package', '_type': 'Assembly' }
        }
      ];

      var sectionIds = getCartSectionIds();
      console.log('[AssemblyBundle] atomic add → kitchen', variantId, '+ assembly', installVariantId);
      var resp = await postAddWithBackoff({
        items: items,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname
      }, 0);

      var data = null;
      try { data = await resp.json(); } catch (e) { /* ignore */ }

      if (!resp.ok) {
        console.warn('[AssemblyBundle] Atomic add failed:', resp.status, data && data.message);
        showFormError(form, data && data.message);
        return;
      }

      flashAdded(button);
      var sections = data && data.sections;
      var count = parseItemCountFromSections(sections);
      dispatchCartUpdate(count, sections);
      // The cart re-render (morph) is async and drops the modal dialog's
      // top-layer state, so re-assert the drawer open AFTER the morph settles.
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          var drawer = document.querySelector('cart-drawer-component');
          var dialog = drawer && drawer.querySelector('dialog');
          if (dialog && !dialog.open) openCartDrawer();
        });
      });
    } catch (err) {
      console.warn('[AssemblyBundle] Error during atomic add:', err);
    } finally {
      form.__assemblyInFlight = false;
      if (button) button.disabled = false;
    }
  }

  function init() {
    if (!getInstallEl()) return; // not an install-required product page

    // The theme delegates on:submit/on:click via document capture, so we attach
    // to WINDOW (capture) to run first and block its handler.
    window.addEventListener('click', function (e) {
      var button = e.target.closest && e.target.closest('.add-to-cart-button, [ref="addToCartButton"]');
      if (!button) return;
      var form = button.closest('form');
      if (!form || !getInstallEl(form)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      triggerAtomicAdd(form, button);
    }, true);

    window.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('form') || !getInstallEl(form)) return;
      e.preventDefault();
      e.stopImmediatePropagation();
      var button = form.querySelector('.add-to-cart-button, [ref="addToCartButton"]');
      triggerAtomicAdd(form, button);
    }, true);

    console.log('[AssemblyBundle] Ready — atomic kitchen + assembly add (native cart)');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
